import React, { useState, useEffect } from 'react';
import { tagsService, Tag } from '../services/tags.service';
import { FiTag, FiEdit2, FiTrash2, FiPlus, FiX, FiTool } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { api } from '../config/api';
import { tw, monoFont } from '../utils/terminalTheme';

interface ServiceType {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
}

export const TagsAndServiceTypes: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [activeTab, setActiveTab] = useState<'tags' | 'serviceTypes'>('tags');
  const [showServiceTypeModal, setShowServiceTypeModal] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState<ServiceType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#6B7280',
    description: '',
    display_order: 0
  });

  useEffect(() => {
    const loadData = async () => {
      loadTags();
      await initializeServiceTypes();
      await loadServiceTypes();
    };
    loadData();
  }, []);

  const loadTags = async () => {
    try {
      setIsLoading(true);
      const data = await tagsService.getTags();
      setTags(data);
    } catch (error) {
      toast.error('Failed to load tags');
    } finally {
      setIsLoading(false);
    }
  };

  const loadServiceTypes = async () => {
    try {
      const response = await api.get('/vendors/service-types/all');
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Failed to load service types:', error);
    }
  };

  const initializeServiceTypes = async () => {
    try {
      const response = await api.post('/vendors/service-types/initialize');
      if (response.data.count > 0) {
        await loadServiceTypes();
      }
    } catch (error) {
      // Ignore if already initialized
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Tag name is required');
      return;
    }

    try {
      const newTag = await tagsService.createTag(formData);
      setTags([...tags, newTag]);
      setShowCreateModal(false);
      resetForm();
      toast.success('Tag created successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    }
  };

  const handleUpdate = async () => {
    if (!editingTag || !formData.name.trim()) return;

    try {
      const updatedTag = await tagsService.updateTag(editingTag.id, formData);
      setTags(tags.map(tag => tag.id === editingTag.id ? updatedTag : tag));
      setEditingTag(null);
      resetForm();
      toast.success('Tag updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update tag');
    }
  };

  const handleDelete = async (tagId: number) => {
    if (!window.confirm('Are you sure you want to delete this tag?')) return;

    try {
      await tagsService.deleteTag(tagId);
      setTags(tags.filter(tag => tag.id !== tagId));
      toast.success('Tag deleted successfully');
    } catch (error) {
      toast.error('Failed to delete tag');
    }
  };

  const handleCreateServiceType = async () => {
    if (!formData.name.trim()) {
      toast.error('Service type name is required');
      return;
    }

    try {
      const response = await api.post('/vendors/service-types', {
        name: formData.name,
        description: formData.description,
        display_order: formData.display_order || 0
      });
      setServiceTypes([...serviceTypes, response.data]);
      setShowServiceTypeModal(false);
      resetForm();
      toast.success('Service type created successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create service type');
    }
  };

  const handleUpdateServiceType = async () => {
    if (!editingServiceType || !formData.name.trim()) return;

    try {
      const response = await api.put(`/vendors/service-types/${editingServiceType.id}`, {
        name: formData.name,
        description: formData.description,
        display_order: formData.display_order
      });
      setServiceTypes(serviceTypes.map(st => st.id === editingServiceType.id ? response.data : st));
      setEditingServiceType(null);
      resetForm();
      toast.success('Service type updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update service type');
    }
  };

  const handleDeleteServiceType = async (id: number) => {
    if (!window.confirm('Are you sure you want to deactivate this service type?')) return;

    try {
      await api.delete(`/vendors/service-types/${id}`);
      await loadServiceTypes();
      toast.success('Service type deactivated');
    } catch (error) {
      toast.error('Failed to delete service type');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      color: '#6B7280',
      description: '',
      display_order: 0
    });
  };

  const openEditModal = (tag: Tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color,
      description: tag.description || '',
      display_order: 0
    });
  };

  const openEditServiceTypeModal = (serviceType: ServiceType) => {
    setEditingServiceType(serviceType);
    setFormData({
      name: serviceType.name,
      color: '#6B7280',
      description: serviceType.description || '',
      display_order: serviceType.display_order
    });
  };

  if (isLoading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
          <span className="text-cyan-400">~/</span>settings/<span className="text-cyan-400">tags</span>
          <span className="text-gray-600 text-sm font-normal ml-3">
            <span className="text-green-500">$</span> manage --all
          </span>
        </h1>
        <p className="text-gray-500 text-sm font-mono mt-1">
          Manage tags for organizing content and service types for vendors
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('tags')}
          className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all flex items-center gap-1.5 ${
            activeTab === 'tags' ? tw.filterActive : tw.filterInactive
          }`}
        >
          <FiTag className="w-3 h-3" />
          Document Tags
        </button>
        <button
          onClick={() => setActiveTab('serviceTypes')}
          className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all flex items-center gap-1.5 ${
            activeTab === 'serviceTypes' ? tw.filterActive : tw.filterInactive
          }`}
        >
          <FiTool className="w-3 h-3" />
          Vendor Service Types
        </button>
      </div>

      {/* Tags Tab */}
      {activeTab === 'tags' && (
        <>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider">Document Tags</h2>
            <button
              onClick={() => setShowCreateModal(true)}
              className={tw.btnPrimary + ' flex items-center gap-2'}
            >
              <FiPlus className="w-3.5 h-3.5" />
              Add Tag
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {tags.map(tag => (
              <div key={tag.id} className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-4 hover:border-cyan-500/30 transition-colors group">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded mr-2"
                      style={{ backgroundColor: tag.color }}
                    />
                    <h3 className="text-sm font-mono font-medium text-white">{tag.name}</h3>
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEditModal(tag)}
                      className="text-gray-500 hover:text-cyan-400 transition-colors"
                    >
                      <FiEdit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
                {tag.description && (
                  <p className="text-gray-500 text-xs font-mono">{tag.description}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Service Types Tab */}
      {activeTab === 'serviceTypes' && (
        <>
          <div className="mb-4 flex justify-between items-center">
            <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider">Vendor Service Types</h2>
            <div className="flex gap-2">
              {serviceTypes.length === 0 && (
                <button
                  onClick={async () => {
                    await initializeServiceTypes();
                    toast.success('Initializing service types...');
                  }}
                  className={tw.btnSecondary}
                >
                  Initialize Defaults
                </button>
              )}
              <button
                onClick={() => setShowServiceTypeModal(true)}
                className={tw.btnPrimary + ' flex items-center gap-2'}
              >
                <FiPlus className="w-3.5 h-3.5" />
                Add Service Type
              </button>
            </div>
          </div>

          <div className={tw.tableWrap}>
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700/30 bg-gray-900/30">
                  <th className={tw.tableHeader}>Order</th>
                  <th className={tw.tableHeader}>Name</th>
                  <th className={tw.tableHeader}>Description</th>
                  <th className={tw.tableHeader}>Status</th>
                  <th className={tw.tableHeaderRight}>Actions</th>
                </tr>
              </thead>
              <tbody className={tw.tableDivider}>
                {serviceTypes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={tw.empty}>
                      No service types found. Click "Initialize Defaults" to add standard service types.
                    </td>
                  </tr>
                ) : (
                  serviceTypes.map(serviceType => (
                  <tr key={serviceType.id} className={tw.tableRow}>
                    <td className={tw.tableCell}>
                      <span className="text-sm text-gray-400 font-mono">{serviceType.display_order}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-sm font-mono font-medium text-white">{serviceType.name}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-sm text-gray-400 font-mono">{serviceType.description || '-'}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono border ${
                        serviceType.is_active
                          ? 'bg-green-500/15 text-green-400 border-green-500/30'
                          : 'bg-red-500/15 text-red-400 border-red-500/30'
                      }`}>
                        {serviceType.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className={tw.tableCellRight}>
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => {
                            openEditServiceTypeModal(serviceType);
                            setShowServiceTypeModal(true);
                          }}
                          className="text-gray-500 hover:text-cyan-400 transition-colors"
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteServiceType(serviceType.id)}
                          className="text-gray-500 hover:text-red-400 transition-colors"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Tag Modal */}
      {(showCreateModal || editingTag) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className={tw.formWrap + ' w-full max-w-md'}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-mono text-cyan-400">
                {editingTag ? 'Edit Tag' : 'Create New Tag'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingTag(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-white transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={tw.label}>Tag Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={tw.input}
                  placeholder="Enter tag name"
                />
              </div>

              <div>
                <label className={tw.label}>Color</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full h-10 bg-gray-900 rounded cursor-pointer border border-gray-700"
                />
              </div>

              <div>
                <label className={tw.label}>Description (optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={tw.input}
                  rows={3}
                  placeholder="Enter description"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingTag(null);
                    resetForm();
                  }}
                  className={tw.btnSecondary}
                >
                  Cancel
                </button>
                <button
                  onClick={editingTag ? handleUpdate : handleCreate}
                  className={tw.btnPrimary}
                >
                  {editingTag ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Type Modal */}
      {(showServiceTypeModal || editingServiceType) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className={tw.formWrap + ' w-full max-w-md'}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-mono text-cyan-400">
                {editingServiceType ? 'Edit Service Type' : 'Create New Service Type'}
              </h2>
              <button
                onClick={() => {
                  setShowServiceTypeModal(false);
                  setEditingServiceType(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-white transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={tw.label}>Service Type Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={tw.input}
                  placeholder="e.g., plumber, electrician"
                />
              </div>

              <div>
                <label className={tw.label}>Display Order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className={tw.input}
                  placeholder="0"
                />
              </div>

              <div>
                <label className={tw.label}>Description (optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={tw.input}
                  rows={3}
                  placeholder="Enter description"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowServiceTypeModal(false);
                    setEditingServiceType(null);
                    resetForm();
                  }}
                  className={tw.btnSecondary}
                >
                  Cancel
                </button>
                <button
                  onClick={editingServiceType ? handleUpdateServiceType : handleCreateServiceType}
                  className={tw.btnPrimary}
                >
                  {editingServiceType ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
